import { api, ApiError } from './client';

/** What a probe proved.
 *
 *  Mirrors `poolprobe.Result` field for field. The shape is deliberately not
 *  just "ok/not ok": a completion served by a still-valid short-lived
 *  credential proves inference and says NOTHING about the durable credential
 *  behind it, and an operator deciding whether to renew a subscription needs
 *  that difference (DR-33). */
export type ProbeOutcome =
  | 'minted'
  | 'stored_credential'
  | 'replaced'
  /** Something else refreshed the credential mid-probe, so this completion
   *  proves nothing about the durable one. */
  | 'concurrent_refresh'
  | 'refused'
  | 'inconclusive'
  /** The call failed for a reason that says nothing about the credential — a
   *  timeout, a 5xx, a bad model name. Kept apart from a refusal because
   *  retiring a working subscription over a timeout is the expensive mistake. */
  | 'did_not_complete';

export interface ProbeResult {
  poolKeyId: string;
  provider?: string;
  /** Absent on a failure — there was no completion to report a model for. */
  model?: string;
  requestedModel?: string;
  reply?: string;
  inputTokens?: number;
  outputTokens?: number;
  latencyMs?: number;
  outcome?: ProbeOutcome;
  detail?: string;
  expiresAt?: string;
  /** The provider ignores an output cap, so this probe spent what it spent. */
  uncappedOutput?: boolean;
  /** Present only on a non-passing result; a pass has no `passed` field. */
  passed?: false;
}

/** passed reports whether the credential actually served something.
 *
 *  A result with no outcome at all is not a pass: the absence of a verdict is
 *  not a verdict, and this is the one screen where guessing optimistically
 *  would be worst. */
const NOT_A_PASS: ProbeOutcome[] = ['refused', 'inconclusive', 'did_not_complete'];

export function passed(r: ProbeResult): boolean {
  return r.passed !== false && Boolean(r.outcome) && !NOT_A_PASS.includes(r.outcome as ProbeOutcome);
}

/** testPoolKey serves one trivial completion through this key.
 *
 *  It SPENDS: the call consumes a shared subscription's quota and can cause a
 *  credential refresh. That is why it is a POST and why the button that calls
 *  it says so. */
export async function testPoolKey(
  id: string,
  opts: { model?: string; maxTokens?: number } = {},
): Promise<ProbeResult> {
  try {
    return await api.post<ProbeResult>(
      `/api/v1/admin/pool-keys/${encodeURIComponent(id)}/test`,
      { model: opts.model ?? '', maxTokens: opts.maxTokens ?? 0 },
    );
  } catch (err) {
    // A provider REFUSING the credential comes back as a 200 with passed:false
    // — it is the answer, not a fault. Anything that lands here is genuinely a
    // failure of ours to ask the question, and reads differently on screen.
    if (err instanceof ApiError) {
      // NOT 'refused'. A 401, a 503 or an unsupported key type says nothing
      // about whether the credential works, and reporting it as a refusal
      // would send an operator to re-seed a subscription that is fine.
      return { poolKeyId: id, passed: false, outcome: 'did_not_complete', detail: err.message };
    }
    throw err;
  }
}
